import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { apiErr } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeftIcon, UserIcon, ClockCounterClockwiseIcon, DownloadSimpleIcon, CircleIcon, SignOutIcon, HourglassIcon, TimerIcon,
} from "@phosphor-icons/react";

const fh = (iso) => (iso ? new Date(iso).toLocaleString("es-HN", { dateStyle: "short", timeStyle: "short" }) : "—");
export const fmtMin = (m) => {
  const n = Math.max(0, Math.round(Number(m) || 0));
  return n < 60 ? `${n} min` : `${Math.floor(n / 60)} h ${String(n % 60).padStart(2, "0")} min`;
};
const ymd = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

// Estado de la sesión: siempre texto + icono (no solo color)
const ESTADOS = {
  activa: { l: "Activa", i: CircleIcon, c: "text-agro-green border-agro-green/40 bg-agro-green/10" },
  cerrada: { l: "Cerrada", i: SignOutIcon, c: "text-text-secondary border-border bg-white/5" },
  expirada: { l: "Expirada", i: HourglassIcon, c: "text-agro-yellow border-agro-yellow/40 bg-agro-yellow/10" },
};
function EstadoSesion({ e }) {
  const s = ESTADOS[e] || ESTADOS.cerrada;
  const I = s.i;
  return <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded border ${s.c}`}><I size={11} weight="bold" /> {s.l}</span>;
}
const CIERRE = { usuario: "Cerró sesión", administrador: "Cerrada por el administrador" };
const finTexto = (s) => (s.estado === "cerrada" ? `${fh(s.fin)} · ${CIERRE[s.cierre] || "Cerrada"}`
  : s.estado === "expirada" ? `${fh(s.expiro_en)} · venció por ${s.motivo_expiracion || "tiempo"} (cierre no detectado)` : "En curso");

function exportarCsv(usuario, items, desde, hasta) {
  const cab = ["Inicio", "Última actividad", "Estado", "Cierre o expiración", "Duración de la sesión (min)", "Tiempo activo estimado (min)", "Dispositivo / navegador", "IP"];
  const filas = items.map((s) => [fh(s.inicio), fh(s.ultima_actividad), ESTADOS[s.estado]?.l || s.estado, finTexto(s), s.duracion_min, s.activo_min, s.dispositivo, s.ip]);
  const csv = [cab, ...filas].map((f) => f.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url; a.download = `historial-acceso-${(usuario.email || "usuario").replace(/[^a-z0-9._-]+/gi, "_")}-${desde}-a-${hasta}.csv`; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function HistorialAcceso({ uid }) {
  const hoy = new Date();
  const [desde, setDesde] = useState(ymd(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - 30)));
  const [hasta, setHasta] = useState(ymd(hoy));
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const cargar = useCallback(() => {
    setError("");
    api.get(`/permissions/users/${uid}/accesos`, { params: { desde, hasta } }).then(({ data: d }) => setData(d)).catch((e) => setError(apiErr(e)));
  }, [uid, desde, hasta]);
  useEffect(() => { cargar(); }, [cargar]);
  const r = data?.resumen;
  const inp = "min-h-[44px] bg-app border border-border rounded-md px-3 py-2 text-sm";

  return (
    <div className="space-y-4" data-testid="historial-acceso">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-text-secondary">Desde<input type="date" value={desde} max={hasta} onChange={(e) => setDesde(e.target.value)} className={`${inp} block mt-1`} data-testid="acc-desde" /></label>
        <label className="text-xs text-text-secondary">Hasta<input type="date" value={hasta} min={desde} onChange={(e) => setHasta(e.target.value)} className={`${inp} block mt-1`} data-testid="acc-hasta" /></label>
        <button type="button" disabled={!data?.items?.length} onClick={() => exportarCsv(data.usuario, data.items, desde, hasta)} data-testid="acc-exportar"
          className="min-h-[44px] px-4 border border-border rounded-md text-sm flex items-center gap-2 disabled:opacity-50"><DownloadSimpleIcon size={16} /> Exportar (CSV)</button>
      </div>

      {error ? <div className="text-sm text-red-300">{error}</div> : !data ? <div className="text-sm text-text-muted">Cargando…</div> : <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="acc-resumen">
          {[["Sesiones", r.sesiones, `${r.activas} activas · ${r.cerradas} cerradas · ${r.expiradas} expiradas`],
            ["Duración de las sesiones", fmtMin(r.duracion_min), "Del inicio al cierre o vencimiento"],
            ["Tiempo activo estimado", fmtMin(r.activo_min), "Minutos con interacción; varias pestañas no se suman dos veces"],
            ["Política de sesión", data.politica ? `${data.politica.inactividad_min} min / ${data.politica.max_horas} h` : "—", "Inactividad / duración máxima (perfiles que no son Administrador)"],
          ].map(([l, v, d]) => (
            <div key={l} className="bg-card border border-border/60 rounded-xl p-3">
              <div className="text-[11px] uppercase tracking-wide text-text-muted">{l}</div>
              <div className="text-lg font-semibold mt-0.5">{v}</div>
              <div className="text-[11px] text-text-muted mt-0.5">{d}</div>
            </div>
          ))}
        </div>

        {data.items.length === 0 ? (
          <div className="bg-card border border-border/60 rounded-xl p-8 text-center text-sm text-text-secondary" data-testid="acc-vacio">Sin sesiones registradas en el período.</div>
        ) : <>
          {/* Móvil: tarjetas */}
          <div className="space-y-2 md:hidden">
            {data.items.map((s) => (
              <div key={s.id} className="bg-card border border-border/60 rounded-xl p-3 text-sm" data-testid={`acc-sesion-${s.id}`}>
                <div className="flex justify-between gap-2"><b>{fh(s.inicio)}</b><EstadoSesion e={s.estado} /></div>
                <div className="text-xs text-text-secondary mt-1">{s.dispositivo}</div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div><span className="text-text-muted">Duración</span><br />{fmtMin(s.duracion_min)}</div>
                  <div><span className="text-text-muted">Activo (est.)</span><br />{fmtMin(s.activo_min)}</div>
                  <div><span className="text-text-muted">Última actividad</span><br />{fh(s.ultima_actividad)}</div>
                  <div><span className="text-text-muted">Cierre / expiración</span><br />{finTexto(s)}</div>
                </div>
              </div>
            ))}
          </div>
          {/* Escritorio: tabla con desplazamiento propio */}
          <div className="hidden md:block bg-card border border-border/60 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-sidebar text-left text-[10px] uppercase tracking-wider text-text-muted">
                <tr><th className="p-3">Inicio</th><th className="p-3">Última actividad</th><th className="p-3">Cierre o expiración</th>
                  <th className="p-3"><TimerIcon size={11} className="inline" /> Duración de la sesión</th><th className="p-3">Tiempo activo (est.)</th>
                  <th className="p-3">Dispositivo / navegador</th><th className="p-3">Estado</th></tr>
              </thead>
              <tbody>
                {data.items.map((s) => (
                  <tr key={s.id} className="border-t border-border/40" data-testid={`acc-fila-${s.id}`}>
                    <td className="p-3 whitespace-nowrap">{fh(s.inicio)}</td>
                    <td className="p-3 whitespace-nowrap">{fh(s.ultima_actividad)}</td>
                    <td className="p-3 text-xs">{finTexto(s)}</td>
                    <td className="p-3 whitespace-nowrap">{fmtMin(s.duracion_min)}</td>
                    <td className="p-3 whitespace-nowrap">{fmtMin(s.activo_min)}</td>
                    <td className="p-3 text-xs">{s.dispositivo}<div className="text-text-muted">{s.ip}</div></td>
                    <td className="p-3"><EstadoSesion e={s.estado} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>}
        <p className="text-[11px] text-text-muted">La duración va del inicio de sesión al cierre o al vencimiento. El tiempo activo es estimado: cuenta los minutos con interacción (clic, tecla, toque o desplazamiento). Si no se detecta el cierre, la sesión queda «Expirada» con la hora en que venció.</p>
      </>}
    </div>
  );
}

/** Política de sesión (solo Administrador General). */
export function PoliticaSesion() {
  const [p, setP] = useState(null);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { api.get("/permissions/politica-sesion").then(({ data }) => setP(data)).catch(() => setP(false)); }, []);
  if (!p) return null;
  const guardar = async (e) => {
    e.preventDefault(); setGuardando(true);
    try { const { data } = await api.put("/permissions/politica-sesion", { inactividad_min: Number(p.inactividad_min), max_horas: Number(p.max_horas) }); setP(data); toast.success("Política de sesión guardada"); }
    catch (er) { toast.error(apiErr(er)); } finally { setGuardando(false); }
  };
  const inp = "min-h-[40px] w-24 bg-app border border-border rounded-md px-3 py-2 text-sm";
  return (
    <form onSubmit={guardar} className="bg-card border border-border/60 rounded-xl p-3 mb-3 flex flex-wrap items-end gap-3 text-xs" data-testid="politica-sesion">
      <div className="flex-1 min-w-[220px]">
        <div className="font-semibold text-sm">Sesión de perfiles que no son Administrador General</div>
        <p className="text-text-muted">Sin «mantener sesión»; el servidor la cierra por inactividad y por duración máxima. Que el navegador no guarde la contraseña requiere equipos o navegadores administrados.</p>
      </div>
      <label className="text-text-secondary">Inactividad (min)<input type="number" min={5} max={240} value={p.inactividad_min} onChange={(e) => setP({ ...p, inactividad_min: e.target.value })} className={`${inp} block mt-1`} data-testid="politica-inactividad" /></label>
      <label className="text-text-secondary">Duración máxima (h)<input type="number" min={1} max={24} value={p.max_horas} onChange={(e) => setP({ ...p, max_horas: e.target.value })} className={`${inp} block mt-1`} data-testid="politica-max" /></label>
      <button type="submit" disabled={guardando} className="min-h-[40px] px-4 bg-agro-green text-black font-semibold rounded-md disabled:opacity-50">Guardar</button>
    </form>
  );
}

/** Registro del usuario: datos y pestaña «Historial de acceso» (página completa, sin ventanas sobre ventanas). */
export default function UsuarioRegistro() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: yo, hasPermission } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "datos" ? "datos" : "accesos";
  const [u, setU] = useState(null);
  const verHistorial = Boolean(yo?.effective?.is_admin || hasPermission("usuarios.historial_acceso"));
  useEffect(() => { api.get("/permissions/users").then(({ data }) => setU(data.find((x) => x.id === id) || false)).catch(() => setU(false)); }, [id]);
  const T = ({ k, icon: I, children }) => (
    <button type="button" onClick={() => setParams(k === "accesos" ? {} : { tab: k }, { replace: true })} aria-pressed={tab === k} data-testid={`usuario-tab-${k}`}
      className={`min-h-[44px] px-4 text-sm flex items-center gap-2 border-b-2 -mb-px ${tab === k ? "border-agro-green text-agro-green font-semibold" : "border-transparent text-text-secondary hover:text-text-primary"}`}>
      <I size={16} /> {children}</button>
  );
  return (
    <Layout title={u ? (u.nombre || u.email) : "Usuario"} subtitle="Registro del usuario">
      <div className="space-y-4 max-w-6xl">
        <button type="button" onClick={() => navigate("/permisos?tab=users")} className="min-h-[44px] text-sm text-text-secondary hover:text-text-primary flex items-center gap-1.5">
          <ArrowLeftIcon size={16} /> Usuarios</button>
        {u === null ? <div className="text-sm text-text-muted">Cargando…</div> : u === false ? <div className="text-sm text-red-300">Usuario no encontrado o sin permiso.</div> : <>
          <div className="flex border-b border-border overflow-x-auto">
            {verHistorial && <T k="accesos" icon={ClockCounterClockwiseIcon}>Historial de acceso</T>}
            <T k="datos" icon={UserIcon}>Datos</T>
          </div>
          {tab === "accesos" && verHistorial ? <HistorialAcceso uid={id} /> : (
            <div className="bg-card border border-border/60 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm" data-testid="usuario-datos">
              <div><div className="text-[11px] uppercase text-text-muted">Nombre</div>{u.nombre || "—"}</div>
              <div><div className="text-[11px] uppercase text-text-muted">Correo o usuario</div>{u.email}</div>
              <div><div className="text-[11px] uppercase text-text-muted">Roles</div>{(u.roles_detail || []).map((r) => r.nombre).join(", ") || "Sin rol"}</div>
              <div><div className="text-[11px] uppercase text-text-muted">Estado</div>{u.estado || "activo"}</div>
              <div><div className="text-[11px] uppercase text-text-muted">Empleado</div>{u.empleado ? `${u.empleado.codigo} · ${u.empleado.puesto || ""}` : "Sin empleado vinculado"}</div>
            </div>
          )}
        </>}
      </div>
    </Layout>
  );
}
